import { VOICE_NAMES } from "../shared/voices";
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { INPUT_MAX_CHARS, type ChapterEnd as ChapterEndView, type StorySummary, type StoryView, type ThingView, type ThreadView } from "../shared/story";
import { api } from "./api";
import { roman } from "../shared/plates";
import { mentions } from "../shared/find";
import { PlateTag } from "./plates";
import { sound } from "./sound";
import { ChapterBand, StoryEnd } from "./endings";
import type { Meta } from "./trust";
import type { StoryUI } from "./useStory";
import { DEFAULT_SKIN, SKINS, type Skin } from "./skins";
import { PenSwitch, PensIntro, ThreadFirst } from "./v2/pens";
import { PocketBar } from "./v2/pocket";

/** Pieces every theme can use. Themes own their layout and CSS; the behaviour lives here and in useStory. */

export { DEFAULT_SKIN, SKINS, type Skin } from "./skins";
const SKIN_KEY = "loom:skin";
export function savedSkin(): Skin {
  try {
    const s = localStorage.getItem(SKIN_KEY);
    return SKINS.some((k) => k.id === s) ? (s as Skin) : DEFAULT_SKIN;
  } catch { return DEFAULT_SKIN; }
}
export function saveSkin(s: Skin) { try { localStorage.setItem(SKIN_KEY, s); } catch { /* per-viewer convenience only */ } }

/** What a theme gets on the home screen (no story yet). */
export interface HomeBits { stories: StorySummary[]; seeds: string[]; open(id: string): void; remove(id: string): Promise<void> }

export interface ThemeProps {
  s: StoryUI;
  /** Set on the home screen until the first reply names the story. */
  home: HomeBits | null;
  skin: Skin;
  setSkin(s: Skin): void;
  goHome(): void;
  /** The album of plates (the dream's chapter images). */
  openPlates(): void;
  /** Privacy, export, delete everything, feedback (trust.tsx). */
  meta(m: Meta): void;
}

export { OPENINGS } from "../shared/story";

/**
 * The note-head: ambient sound on or off (off by default), with a volume slider while on. Remembered on this device;
 * if it was on, sound resumes on the first tap of the next visit (browsers start audio only after one).
 */
const SOUND_KEY = "loom:sound";
export function SoundToggle({ className = "" }: { className?: string }) {
  const [on, setOn] = useState(false);
  const [vol, setVol] = useState(() => { try { return Number(localStorage.getItem(`${SOUND_KEY}:vol`) ?? 0.8); } catch { return 0.8; } });
  useEffect(() => {
    let wanted = false;
    try { wanted = localStorage.getItem(SOUND_KEY) === "on"; } catch { /* off */ }
    if (!wanted) return;
    const first = () => { sound.enable(); sound.volume(vol); setOn(true); };
    window.addEventListener("pointerdown", first, { once: true });
    return () => window.removeEventListener("pointerdown", first);
  }, []);
  const [music, setMusic] = useState(() => { try { return localStorage.getItem(`${SOUND_KEY}:music`) !== "off"; } catch { return true; } });
  useEffect(() => { sound.music(music); }, [music]);
  const toggleMusic = () => {
    setMusic((m) => { try { localStorage.setItem(`${SOUND_KEY}:music`, m ? "off" : "on"); } catch { /* this visit only */ } return !m; });
  };
  const toggle = () => {
    if (on) { sound.disable(); setOn(false); } else { sound.enable(); sound.volume(vol); setOn(true); }
    try { localStorage.setItem(SOUND_KEY, on ? "off" : "on"); } catch { /* this visit only */ }
  };
  return (
    <span className={`lm-sound ${on ? "lm-sound-on" : ""} ${className}`}>
      <button type="button" onClick={toggle} aria-pressed={on} aria-label={on ? "Turn ambient sound off" : "Turn ambient sound on"} title={on ? "Sound on" : "Sound off"}>
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 17.5V5.2l10-2v11.3" fill="none" stroke="currentColor" strokeWidth="1.6" /><ellipse cx="6.6" cy="17.6" rx="2.6" ry="2" fill="currentColor" /><ellipse cx="16.6" cy="15.4" rx="2.6" ry="2" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.4" />{!on && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.4" />}</svg>
      </button>
      {on && (
        <input type="range" min={0} max={1} step={0.05} value={vol} aria-label="Sound volume"
          onChange={(e) => { const v = Number(e.target.value); setVol(v); sound.volume(v); try { localStorage.setItem(`${SOUND_KEY}:vol`, String(v)); } catch { /* fine */ } }} />
      )}
      {on && (
        <button type="button" className="lm-music" aria-pressed={music} onClick={toggleMusic} title={music ? "Music on: the score under the story" : "Music off: places only"}>
          {music ? "music" : "places only"}
        </button>
      )}
    </span>
  );
}

/** The themes side by side; on a phone, one button that moves to the next (so it never takes a row of its own). */
export function SkinPicker({ skin, setSkin, className = "" }: { skin: Skin; setSkin(s: Skin): void; className?: string }) {
  const i = SKINS.findIndex((k) => k.id === skin);
  const next = SKINS[(i + 1) % SKINS.length]!;
  return (
    <>
      <div className={`lm-skins ${className}`} role="radiogroup" aria-label="Theme">
        {SKINS.map((k) => (
          <button key={k.id} type="button" role="radio" aria-checked={skin === k.id} title={k.hint} onClick={() => setSkin(k.id)}>{k.name}</button>
        ))}
      </div>
      <button type="button" className={`lm-skins-cycle ${className}`} onClick={() => setSkin(next.id)} aria-label={`Theme: ${SKINS[i]?.name}. Switch to ${next.name}`}>
        {SKINS[i]?.name} ›
      </button>
    </>
  );
}

/** The line as sent: with the Thread pen, a weave (a line that is already a command goes as typed). */
const penned = (s: StoryUI) => (s.pen === "thread" && s.draft.trim() && !s.draft.trimStart().startsWith("/") ? `/weave ${s.draft.trim()}` : s.draft);

/** The one line to write in. Enter sends (and cuts the reply where you stopped reading); shift+Enter is a new line. */
export function Writer({ s, className = "", label, sendLabel, placeholder, prompt }: {
  s: StoryUI; className?: string; label?: string; sendLabel: ReactNode; placeholder?: string; prompt?: ReactNode;
}) {
  const chapter = s.view?.chapter;
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (!s.busy && s.id) ref.current?.focus({ preventScroll: true }); }, [s.busy, s.id]);
  // Where you are, one tap away: the place chip opens "Where you are" (the cards, what you carry, the map).
  const hereName = s.view?.here ? s.view.things.find((t) => t.id === s.view!.here)?.name : undefined;
  return (
    <form className={`${className}${chapter ? " lm-has-chapter" : ""}${s.pen === "thread" ? " v2-writing-thread" : ""}`} onSubmit={(e: FormEvent) => { e.preventDefault(); s.send(penned(s)); }}>
      {hereName && (
        <p className="lm-here-line">
          {/* A span, not a button: the themes style every button in the writer as the round send button. */}
          <span role="button" tabIndex={0} className="lm-here-chip" aria-label={`Where you are: ${hereName}. What's here, what you carry, the map`}
            onClick={() => window.dispatchEvent(new Event("weftwake:here"))}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); window.dispatchEvent(new Event("weftwake:here")); } }}>
            <span aria-hidden="true">◎</span> {hereName}
          </span>
        </p>
      )}
      {chapter && (
        <p className={`lm-chapter-line ${chapter.crisis ? "lm-chapter-crisis" : ""}`}>
          {/* A span, not a button: the themes style every button in the writer as the round send button. */}
          <span className="lm-chapter-n lm-chapter-open" role="button" tabIndex={0} title="Your journal: the chapters"
            onClick={() => window.dispatchEvent(new CustomEvent("weftwake:map", { detail: "chapters" }))}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); window.dispatchEvent(new CustomEvent("weftwake:map", { detail: "chapters" })); } }}>
            Chapter {roman(chapter.n)}
          </span> {chapter.question}
          {s.view?.finale && <strong> · the finale</strong>}
          {chapter.crisis && <strong> · the moment of decision</strong>}
        </p>
      )}
      <label className="sr" htmlFor="lm-input">{label ?? (s.id ? "What you do or say" : "The first line of your story")}</label>
      {prompt}
      <AiNotice s={s} />
      <PensIntro s={s} />
      <LineHint s={s} />
      <VerbMenu s={s} />
      <PenSwitch s={s} />
      <PocketBar s={s} />
      {s.freeLeft !== null && (
        <p className="lm-freeleft">
          {s.freeLeft === 0 ? "Your free turns are used." : `${s.freeLeft} free ${s.freeLeft === 1 ? "turn" : "turns"} left.`}{" "}
          <span role="button" tabIndex={0} className="lm-freeleft-go" onClick={() => window.dispatchEvent(new CustomEvent("weftwake:patron", { detail: { reason: null } }))}
            onKeyDown={(e) => { if (e.key === "Enter") window.dispatchEvent(new CustomEvent("weftwake:patron", { detail: { reason: null } })); }}>Become a Patron</span>
        </p>
      )}
      <textarea
        ref={ref}
        id="lm-input"
        rows={1}
        value={s.draft}
        disabled={s.loading}
        maxLength={INPUT_MAX_CHARS}
        placeholder={placeholder ?? s.placeholder}
        onChange={(e) => s.onType(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key !== "Enter" || e.shiftKey) return;
          e.preventDefault();
          // In the "/" menu, Enter completes the one command the typing points at; it never sends half a command.
          const only = verbsFor(s.draft);
          if (only.length === 1 && s.draft.trim() !== only[0]!.cmd) { pickVerb(s, only[0]!); return; }
          s.send(penned(s));
        }}
      />
      {s.id && showsVerbs(s) && (
        <span role="button" tabIndex={0} className="lm-verb-open" aria-label="Commands that shape the world"
          onClick={() => s.fill("/")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); s.fill("/"); } }}>/</span>
      )}
      <button type="submit" disabled={!s.draft.trim() || s.busy} aria-label={s.id ? "Send" : "Begin"}>{sendLabel}</button>
      <ThreadFirst s={s} />
      {!s.id && !s.live && !s.pending && <SceneFold s={s} />}
    </form>
  );
}

/** Crisis resources, shown instead of the story until the player says they're okay. */
export function Care({ s, className = "" }: { s: StoryUI; className?: string }) {
  const ref = useRef<HTMLElement>(null);
  // The card can land below the fold (after a long reply): bring it into view and give it focus.
  useEffect(() => {
    if (!s.resources || !ref.current) return;
    ref.current.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    ref.current.focus({ preventScroll: true });
  }, [!!s.resources]);
  if (!s.resources) return null;
  return (
    <section ref={ref} tabIndex={-1} className={`lm-care ${className}`} role="alert">
      <p>Before the story goes on: if any of that is about you, you don't have to carry it alone.</p>
      <ul>
        {s.resources.map((r) => (
          <li key={r.name}>
            <a href={r.href}>{r.name}</a> <span>{r.detail}</span>
            {r.alt && <> · <a href={r.alt.href}>{r.alt.label}</a></>}
          </li>
        ))}
      </ul>
      <button type="button" onClick={s.okay}>I'm okay, back to the story</button>
      <p className="lm-gate-small"><a href="/safety" target="_blank" rel="noopener">How Weftwake handles this</a></p>
    </section>
  );
}

/** Makes the first mention of each known thing in a paragraph tappable ("look closer"). */
export { inkOf, type Ink } from "./ink";
import type { Ink } from "./ink";

export function linkify(text: string, things: readonly Pick<ThingView, "name">[], onLook: ((name: string) => void) | null, cls = "lm-thing", ink?: (name: string) => Ink): ReactNode[] {
  if (!onLook) return [text];
  const names = things.map((t) => t.name.replace(/^(the|a|an)\s+/i, "")).filter((n) => n.length > 2)
    .sort((a, b) => b.length - a.length);
  if (!names.length) return [text];
  const re = new RegExp(`\\b(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`, "gi");
  const out: ReactNode[] = [];
  const seen = new Set<string>();
  let last = 0;
  for (const m of text.matchAll(re)) {
    const key = m[0].toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text.slice(last, m.index));
    out.push(<button key={m.index} type="button" className={`${cls}${ink ? ` lm-ink-${ink(m[0])}` : ""}`} onClick={() => onLook(m[0])}>{m[0]}</button>);
    last = m.index! + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

// ---- free graphics from the world graph (no model calls) ------------------------------------------

/** Word-wraps to `width` columns. */
export function wrap(text: string, width: number): string[] {
  const out: string[] = [];
  let line = "";
  for (const w of text.split(/\s+/).filter(Boolean)) {
    if ((line + " " + w).trim().length > width) { if (line) out.push(line); line = w.length > width ? w.slice(0, width) : w; }
    else line = (line + " " + w).trim();
  }
  if (line) out.push(line);
  return out;
}

/** A boxed ASCII card for a found object: its name on top, its hint inside. */
export function asciiCard(title: string, body: string, width = 26): string {
  const inner = width - 4;
  const t = title.toUpperCase();
  const head = t.length > inner - 1 ? `${t.slice(0, inner - 2).trimEnd()}…` : t;
  const lines = wrap(body, inner).slice(0, 4);
  const pad = (s: string) => s + " ".repeat(Math.max(0, inner - s.length));
  return [
    `┌─ ${head} ${"─".repeat(Math.max(0, inner - head.length - 1))}┐`,
    ...lines.map((l) => `│ ${pad(l)} │`),
    `└${"─".repeat(inner + 2)}┘`,
  ].join("\n");
}

/** An ASCII tree of the world's places and what is inside them, newest place first; "@" marks the latest one. */
export function asciiMap(things: readonly ThingView[], max = 14, hereId?: string | null): string {
  const byParent = new Map<string, ThingView[]>();
  for (const t of things) {
    const k = t.parent ?? "";
    byParent.set(k, [...(byParent.get(k) ?? []), t]);
  }
  // Where the player is, as the canon pass read it; a story from before that guesses the place seen last.
  const here = hereId ?? [...things].filter((t) => t.kind === "place").sort((a, b) => Math.max(...b.seen) - Math.max(...a.seen))[0]?.id;
  const lines: string[] = [];
  const walk = (t: ThingView, prefix: string, last: boolean, depth: number) => {
    if (lines.length >= max) return;
    const mark = t.kind === "place" ? "" : t.kind === "person" ? "☺ " : "· ";
    lines.push(`${depth ? prefix + (last ? "└─ " : "├─ ") : ""}${mark}${t.name}${t.id === here ? "  @ you" : ""}`);
    const kids = (byParent.get(t.id) ?? []).slice(0, 5);
    kids.forEach((k, i) => walk(k, depth ? prefix + (last ? "   " : "│  ") : "", i === kids.length - 1, depth + 1));
  };
  const roots = (byParent.get("") ?? []).filter((t) => t.kind === "place" || byParent.has(t.id))
    .sort((a, b) => Math.max(...b.seen) - Math.max(...a.seen));
  for (const r of roots) walk(r, "", true, 0);
  return lines.join("\n");
}

/** Block-character sparkline of tension (1–5) over the turns. */
export function tensionSpark(turns: readonly { tension?: number }[]): string {
  const bars = " ▁▂▃▄▅▆▇█";
  return turns.map((t) => bars[Math.max(1, Math.min(8, Math.round(((t.tension ?? 3) - 1) / 4 * 7) + 1))]).join("");
}

/** How long a thread has been quiet, in turns. */
export const quietFor = (t: ThreadView, now: number) => now - Math.max(...t.seen);

/** How frayed a quiet thread is: 0 while it sounds, 1–3 the longer it stays quiet. */
export const frayOf = (t: ThreadView, now: number): 0 | 1 | 2 | 3 => {
  if (t.state !== "open") return 0;
  const q = quietFor(t, now);
  return q >= 10 ? 3 : q >= 6 ? 2 : q >= 3 ? 1 : 0;
};

/** Threads ordered for the margins: open first (most recently seen first), then closed. */
export function threadOrder(threads: readonly ThreadView[]): ThreadView[] {
  return [...threads].sort((a, b) => (a.state === b.state ? Math.max(...b.seen) - Math.max(...a.seen) : a.state === "open" ? -1 : 1));
}

/** The chapter images that belong after turn `at` (drawn by the nightly dream; private to this player). */
export function Plates({ s, at, className = "" }: { s: StoryUI; at: number; className?: string }) {
  const imgs = (s.view?.images ?? []).filter((i) => i.at === at);
  if (!s.id || !imgs.length) return null;
  return (
    <>
      {imgs.map((img) => (
        <figure key={img.i} className={`lm-plate ${className}`}>
          <img src={api.imageUrl(s.id!, img.i)} alt={img.caption} loading="lazy" />
          <figcaption>{img.caption}</figcaption>
          {img.code && <PlateTag src={api.imageUrl(s.id!, img.i)} plate={{ i: img.i, code: img.code, caption: img.caption }} />}
        </figure>
      ))}
    </>
  );
}

/**
 * The cut as a ceremony: right after the player cuts in, the part of the reply they never saw unravels on screen for
 * a moment; afterwards the turn keeps a quiet count ("41 words unwoven"). Themes name the loss in their own words.
 */
export function Unwoven({ s, t, label = (n) => `${n} ${n === 1 ? "word" : "words"} unwoven`, className = "" }: {
  s: StoryUI; t: { n: number; unwoven?: number }; label?: (n: number) => string; className?: string;
}) {
  const rest = s.unravel?.n === t.n ? s.unravel.text : null;
  return (
    <>
      {rest && <Unravel text={rest} className={className} />}
      {!!t.unwoven && <span className={`lm-unwoven ${rest ? "lm-unwoven-new" : ""} ${className}`}>{label(t.unwoven)}</span>}
    </>
  );
}

/**
 * The erasure itself. Every word (and space) gets two delays spread over UNRAVEL_SPREAD_MS: --f in reading order and
 * --b backwards from the end. Each theme's CSS picks one: Loom unweaves backwards to the cut, so the kept text never
 * moves; Manuscript draws an ink line through in reading order, then lifts the struck words away from the end;
 * Phosphor decays backwards like a backspace. Each word folds to nothing as it goes, so the page closes up gently.
 */
export const UNRAVEL_SPREAD_MS = 1800;
export function Unravel({ text, className = "" }: { text: string; className?: string }) {
  const parts = text.trim().split(/(\s+)/).filter(Boolean);
  const last = Math.max(1, parts.length - 1);
  return (
    <span className={`lm-unravel ${className}`} aria-hidden="true">
      {parts.map((w, i) => (
        <span key={i} style={{ "--f": `${Math.round((i / last) * UNRAVEL_SPREAD_MS)}ms`, "--b": `${Math.round(((last - i) / last) * UNRAVEL_SPREAD_MS)}ms` } as React.CSSProperties}>{w}</span>
      ))}
    </span>
  );
}

/** "Chapter II ends", in each theme's words. */
function chapterEnds(n: number): string {
  const skin = document.documentElement.dataset.skin;
  // The word stays "chapter" everywhere (the guide and the line above the writer use it); the theme adds its flourish.
  return skin === "nitrate" ? `Chapter ${roman(n)} · end of reel` : skin === "cyanotype" ? `Chapter ${roman(n)} · the print is fixed` : skin === "edition" ? `Chapter ${roman(n)} · late final` : `Chapter ${roman(n)} ends`;
}

/** The end of a chapter: its number, its question, and how it ended. */
export const OUTCOME_LABEL = { earned: "Earned", turned: "Answered, at a price", lost: "Lost" } as const;
type Ends = { n: number; chapterEnd?: ChapterEndView; storyEnd?: { question: string; outcome: "earned" | "turned" | "lost" } };
export function ChapterEnd({ s, t, className = "" }: { s: StoryUI; t: Ends; className?: string }) {
  const c = t.chapterEnd;
  if (!c) return null;
  const view = s.view;
  // Only the latest chapter's card offers an ending, and only while none is under way.
  const latest = view ? Math.max(0, ...view.turns.filter((x) => x.chapterEnd).map((x) => x.chapterEnd!.n)) === c.n : false;
  const offer = latest && !!view && !view.finale && !view.ended && !t.storyEnd;
  return (
    <>
      <ChapterBand s={s} n={c.n} at={t.n} outcome={c.outcome} />
      <aside className={`lm-chapter-end lm-chapter-${c.outcome} ${className}`} aria-label={`Chapter ${c.n} ends: ${OUTCOME_LABEL[c.outcome]}`}>
        <p className="lm-chapter-eyebrow">{chapterEnds(c.n)}</p>
        <p className="lm-chapter-q">{c.question}</p>
        <p className="lm-chapter-outcome">{OUTCOME_LABEL[c.outcome]}</p>
        {/* What it cost and what the player had to work with (game review 2: "the price was never named"). */}
        {c.price && c.outcome !== "earned" && <p className="lm-chapter-price">{c.outcome === "lost" ? "Lost: " : "The price: "}{c.price}</p>}
        {c.leverage?.length ? <p className="lm-chapter-leverage">You had: {c.leverage.join(" · ")}</p> : null}
        {offer && (
          <div className="lm-chapter-offer">
            {c.suggestEnd && <p>This could be where the story ends. Or it can go on.</p>}
            <button type="button" onClick={() => window.dispatchEvent(new Event("weftwake:finale"))}>Bring the story to an end</button>
          </div>
        )}
        {/* Pass it on: give this opening to someone (their chapter cards and yours, side by side). */}
        {latest && !t.storyEnd && (
          <p className="lm-chapter-pass"><button type="button" className="lm-linkish" onClick={() => window.dispatchEvent(new Event("weftwake:passon"))}>Pass this opening on</button></p>
        )}
      </aside>
      {t.storyEnd && <StoryEnd s={s} t={t} />}
    </>
  );
}

export { mentions };

/**
 * The narrator is about to write (owner: "an animated signal that the AI is thinking"): a shuttle passing along a
 * thread while three weft points light in turn. Shown from the moment the player sends until the first words appear.
 */
export function Weaving({ label, className = "" }: { label: string; className?: string }) {
  return (
    <p className={`lm-weaving ${className}`} role="status">
      <span className="lm-weave" aria-hidden="true"><i /><i /><i /><b /></span>
      <span className="lm-weaving-label">{label}</span>
    </p>
  );
}
/** From sending until the reply's first words show. */
export const waiting = (s: StoryUI) => s.busy || (!!s.live?.streaming && !s.live.text);

/**
 * The AI notice (owner's compliance list, after California's SB 243): at the start of each session in a story and
 * again after every three hours of play, a plain line that the narrator is an AI. Dismissing it starts the clock.
 */
const AI_NOTICE_KEY = "loom:ai-notice", AI_NOTICE_EVERY = 3 * 3_600_000;
const sessionSeen = () => { try { return sessionStorage.getItem(AI_NOTICE_KEY) === "1"; } catch { return false; } };
function AiNotice({ s }: { s: StoryUI }) {
  const [due, setDue] = useState(() => {
    try { return !sessionSeen() || Date.now() - Number(localStorage.getItem(AI_NOTICE_KEY) ?? 0) > AI_NOTICE_EVERY; } catch { return true; }
  });
  // A long session: check again every ten minutes.
  useEffect(() => {
    const t = window.setInterval(() => {
      try { if (Date.now() - Number(localStorage.getItem(AI_NOTICE_KEY) ?? 0) > AI_NOTICE_EVERY) setDue(true); } catch { /* shown as due */ }
    }, 600_000);
    return () => window.clearInterval(t);
  }, []);
  if (!due || !s.id) return null;
  const ok = () => {
    try { localStorage.setItem(AI_NOTICE_KEY, String(Date.now())); sessionStorage.setItem(AI_NOTICE_KEY, "1"); } catch { /* shown again next time */ }
    setDue(false);
  };
  return (
    <p className="lm-ai-notice" role="note">
      You're playing with an AI. The narrator, its replies and the story's images are generated by AI, not written by a person.{" "}
      <span role="button" tabIndex={0} className="lm-line-hint-x" onClick={ok} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); ok(); } }}>got it</span>
    </p>
  );
}

/**
 * Commands that shape the world (co-creation step 2): typed at the start of the line, or picked from the "/" menu
 * that opens as soon as the line starts with "/". /journal opens the journal (no turn); the others are moves (a turn each).
 */
export const VERBS = [
  { cmd: "/ask", fill: "/ask ", gloss: "Ask the world a question. It answers inside the story: yes, no, yes but…, or no and…" },
  { cmd: "/elsewhere", fill: "/elsewhere ", gloss: "Go somewhere else, or later. Time passes while you're away." },
  { cmd: "/trouble", fill: "/trouble", gloss: "Ask for trouble: something happens now, the world's choice. Once a chapter." },
  { cmd: "/clock", fill: "/clock until ", gloss: "Set a clock of your own (\"until dawn\"). Something happens when it runs out." },
  { cmd: "/flashback", fill: "/flashback ", gloss: "Something you did earlier, true now: it helps, at a price. Once a chapter." },
  { cmd: "/weave", fill: "/weave ", gloss: "The Thread pen: make something true now. The world weaves something back. Three a chapter." },
  { cmd: "/journal", fill: "/journal", gloss: "Your journal: threads, places you've been, what you've kept, chapters. Doesn't use a turn." },
] as const;
type VerbItem = (typeof VERBS)[number];
/** The commands the line's start points at (only while the line is "/" plus part of a command word). */
export const verbsFor = (draft: string): VerbItem[] => {
  const t = draft.trimStart();
  return /^\/[a-z]*$/i.test(t) ? VERBS.filter((v) => v.cmd.startsWith(t.toLowerCase())) : [];
};
function pickVerb(s: StoryUI, v: VerbItem) { if (v.cmd === "/journal") s.send("/journal"); else s.fill(v.fill); }
/** The "/" button shows once the story is under way (a first-time player meets none of this), or once used. */
const showsVerbs = (s: StoryUI) => s.turns.length >= 4 || s.turns.some((t) => /^\/(ask|elsewhere|trouble|clock|flashback|weave)\b/i.test(t.input));

function VerbMenu({ s }: { s: StoryUI }) {
  const list = verbsFor(s.draft);
  if (!s.id || !list.length) return null;
  return (
    <ul className="lm-verbs" role="listbox" aria-label="Commands">
      {list.map((v) => (
        <li key={v.cmd} role="option" aria-selected={list.length === 1} tabIndex={0}
          onMouseDown={(e) => e.preventDefault()} onClick={() => pickVerb(s, v)}
          onKeyDown={(e) => { if (e.key === "Enter") pickVerb(s, v); }}>
          <strong>{v.cmd}</strong> <span>{v.gloss}</span>
        </li>
      ))}
    </ul>
  );
}

/** A quiet mark after a line of the player's that the world took as true; tap it to see what was kept. */
export function Kept({ t, className = "" }: { t: { kept?: string[] }; className?: string }) {
  const [open, setOpen] = useState(false);
  if (!t.kept?.length) return null;
  return (
    <>
      {" "}
      <span role="button" tabIndex={0} className={`lm-kept ${className}`} aria-expanded={open} title="Your words became part of the world"
        onClick={() => setOpen((x) => !x)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((x) => !x); } }}>kept</span>
      {open && <span className="lm-kept-list"> Kept as true: {t.kept.join(" · ")}.</span>}
    </>
  );
}

/** A break before a scene the player framed with /elsewhere. */
export function SceneBreak({ t, className = "", mark = "⁂" }: { t: { verb?: string }; className?: string; mark?: string }) {
  return t.verb === "elsewhere" ? <p className={`lm-scene-break ${className}`} aria-hidden="true">{mark}</p> : null;
}

/** The turn where the chapter's moment of decision arrived (game review 2: "mark the crisis turn in every theme"). */
export function Decision({ t, className = "", label = "The moment of decision" }: { t: { decision?: true }; className?: string; label?: string }) {
  return t.decision ? <p className={`lm-decision ${className}`}>{label}</p> : null;
}

/** The narrator's voice turned with the story at this turn (worker/voice-turn.ts): a quiet line above the reply. */
export function VoiceShift({ t, className = "", label = (name) => `The telling turns ${name}.` }: { t: { voiceShift?: { from: string; to: string } }; className?: string; label?(name: string): string }) {
  return t.voiceShift ? <p className={`lm-voice-shift ${className}`}>{label(VOICE_NAMES[t.voiceShift.to as keyof typeof VOICE_NAMES] ?? t.voiceShift.to)}</p> : null;
}

/** A dormant thread the dream planned came back in this turn (shown once the prose names it: shared/find.ts mentions). */

export function Returned({ t, className = "", label = (name) => `↺ ${name} comes back` }: { t: { callback?: string; prose?: string; text?: string }; className?: string; label?(name: string): string }) {
  const prose = t.prose ?? t.text ?? "";
  return t.callback && mentions(prose, t.callback) ? <p className={`lm-returned ${className}`}>{label(t.callback)}</p> : null;
}

/** Press and hold the reply being written to make the hand write faster; let go and it slows again. */
export function holdToHurry(s: StoryUI) {
  const stop = () => s.hurry(false);
  return {
    onPointerDown: (e: React.PointerEvent) => { if (e.button === 0) s.hurry(true); },
    onPointerUp: stop, onPointerCancel: stop, onPointerLeave: stop,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    "data-hurry": s.hurrying ? "1" : undefined,
    title: "Hold to write faster",
  };
}

/**
 * Teaching the moves in play (game review 2), one hint at a time, each shown where its move happens and retired once
 * the player has used it, or after a few showings (remembered on this device): under the reply being written, hold to
 * hurry for the first replies, then from turn 3 how to cut in; above the line, tap a name to look closer (the newest
 * name pulses), then bring back a thread that has gone quiet.
 */
const HINT_KEY = "loom:hurry-hint", CUT_KEY = "loom:cut-hint", LOOK_KEY = "loom:look-hint", PULL_KEY = "loom:pull-hint", SLASH_KEY = "loom:slash-hint";
const SHOWINGS = 3, LEARNED = 99;
const hintCount = (k: string) => { try { return Number(localStorage.getItem(k) ?? 0); } catch { return LEARNED; } };
const setHint = (k: string, v: number) => { try { localStorage.setItem(k, String(v)); } catch { /* shown again: fine */ } };
/** A thread quiet this long gets the pull hint. */
const QUIET_HINT = 6;

export function HurryHint({ s, className = "" }: { s: StoryUI; className?: string }) {
  const n = s.live?.n;
  // Which hint this reply carries is decided once, when it starts.
  const [kind] = useState<"hurry" | "cut" | null>(() => {
    if (n !== undefined && n >= 3 && hintCount(CUT_KEY) < SHOWINGS) return "cut";
    return hintCount(HINT_KEY) < SHOWINGS ? "hurry" : null;
  });
  useEffect(() => { if (kind && n !== undefined) setHint(kind === "cut" ? CUT_KEY : HINT_KEY, hintCount(kind === "cut" ? CUT_KEY : HINT_KEY) + 1); }, [kind, n]);
  useEffect(() => { if (s.hurrying) setHint(HINT_KEY, LEARNED); }, [s.hurrying]);
  useEffect(() => { if (s.turns.some((t) => t.status === "cut")) setHint(CUT_KEY, LEARNED); }, [s.turns]);
  if (!kind || !s.live?.streaming || (kind === "hurry" && s.hurrying)) return null;
  return (
    <p className={`lm-hurry-hint ${className}`}>
      {kind === "cut" ? "Type now to cut in: the story stops where you are reading and follows your words"
        : "Press and hold the words (finger or mouse button) to speed them up · start typing and the narrator waits"}
    </p>
  );
}

/** Above the line, between replies: look closer (the newest name pulses), then pulling a quiet thread. */
function LineHint({ s }: { s: StoryUI }) {
  const view = s.view;
  const settled = !!view && !s.live && !s.pending && !s.busy && s.turns.length > 0;
  const quiet = view?.threads.filter((t) => t.state === "open" && t.seen.length && quietFor(t, view.n) >= QUIET_HINT)
    .sort((a, b) => quietFor(b, view.n) - quietFor(a, view.n))[0];
  const [, bump] = useState(0);
  const kind = !settled ? null : view!.things.length && hintCount(LOOK_KEY) < SHOWINGS ? "look" : quiet && hintCount(PULL_KEY) < SHOWINGS ? "pull"
    : showsVerbs(s) && hintCount(SLASH_KEY) < SHOWINGS ? "slash" : null;
  const keyOf = (k: string) => (k === "look" ? LOOK_KEY : k === "pull" ? PULL_KEY : SLASH_KEY);
  const lastN = s.turns.at(-1)?.n;
  useEffect(() => { if (s.looking || Object.keys(s.looks).length) setHint(LOOK_KEY, LEARNED); }, [s.looking, s.looks]);
  useEffect(() => { if (s.turns.some((t) => t.pulled)) setHint(PULL_KEY, LEARNED); }, [s.turns]);
  useEffect(() => { if (s.turns.some((t) => /^\/(ask|elsewhere)\b/i.test(t.input))) setHint(SLASH_KEY, LEARNED); }, [s.turns]);
  // Counted once per turn it is shown on, and only once it has been on screen a moment (reopening or reloading a story
  // doesn't use up its showings); the newest name in the story pulses for a few seconds.
  useEffect(() => {
    if (!kind || lastN === undefined) return;
    const at = `${s.id}:${lastN}`, seenKey = `${keyOf(kind)}:at`;
    const count = window.setTimeout(() => {
      try { if (localStorage.getItem(seenKey) === at) return; localStorage.setItem(seenKey, at); } catch { /* counted anyway */ }
      setHint(keyOf(kind), hintCount(keyOf(kind)) + 1);
    }, 2000);
    if (kind !== "look") return () => window.clearTimeout(count);
    const names = document.querySelectorAll<HTMLElement>(".lm-thing");
    const el = names[names.length - 1];
    el?.classList.add("lm-pulse");
    const t = window.setTimeout(() => el?.classList.remove("lm-pulse"), 6000);
    return () => { window.clearTimeout(count); window.clearTimeout(t); el?.classList.remove("lm-pulse"); };
  }, [kind, lastN]);
  if (!kind) return null;
  const dismiss = () => { setHint(keyOf(kind), LEARNED); bump((x) => x + 1); };
  return (
    <p className="lm-line-hint">
      {kind === "look" ? "Tap a name in the story to look closer at it." : kind === "slash" ? (s.turns.some((t) => t.kept?.length)
        ? "Your words became part of the world (marked “kept”). Start a line with / to shape it on purpose: /ask the world a question, or go /elsewhere."
        : "Start a line with /ask to ask the world a question, or /elsewhere to go somewhere else, or later. Type help for the short list.") : (
        <>“{quiet!.name}” has gone quiet.{" "}
          <span role="button" tabIndex={0} className="lm-line-hint-go" onClick={() => s.pull(quiet!)}
            onKeyDown={(e) => { if (e.key === "Enter") s.pull(quiet!); }}>Bring it back</span></>
      )}{" "}
      <span role="button" tabIndex={0} className="lm-line-hint-x" aria-label="Got it, don't show this again" onClick={dismiss}
        onKeyDown={(e) => { if (e.key === "Enter") dismiss(); }}>got it</span>
    </p>
  );
}

/**
 * A long-press on a settled paragraph reveals every name in it for a few seconds (dry ink shows its line again).
 * One listener for every theme; the reply being written is left to hold-to-hurry.
 */
export function useInkReveal(): void {
  useEffect(() => {
    let timer = 0;
    const down = (e: PointerEvent) => {
      const p = (e.target as Element | null)?.closest?.(".lw-prose, .ms-print, .ph-prose");
      if (!p || p.closest("[data-live]") || p.hasAttribute("data-live")) return;
      timer = window.setTimeout(() => {
        p.setAttribute("data-reveal", "");
        window.setTimeout(() => p.removeAttribute("data-reveal"), 3000);
      }, 450);
    };
    const cancel = () => window.clearTimeout(timer);
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointerup", cancel);
    window.addEventListener("pointercancel", cancel);
    const move = (e: PointerEvent) => { if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) cancel(); };
    window.addEventListener("pointermove", move);
    return () => {
      window.removeEventListener("pointerdown", down); window.removeEventListener("pointerup", cancel);
      window.removeEventListener("pointercancel", cancel); window.removeEventListener("pointermove", move);
    };
  }, []);
}

/** Opens a story at the player's latest turn (themes mark each turn with data-turn). */
export function scrollToLatest(n: number): void {
  const el = document.querySelector(`[data-turn="${n}"]`);
  if (el) el.scrollIntoView({ block: "start" });
}

/** The link to the album, as a theme wants it labelled. */
export function PlatesLink({ open, label = "plates" }: { open(): void; label?: string }) {
  return <a href="/plates" onClick={(e) => { e.preventDefault(); open(); }}>{label}</a>;
}

/** The nearest scrolling ancestor of an element, or null for the window. */
export function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === "auto" || o === "scroll") && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * Steady reading (owner, 2026-10-31: "as it finishes writing each new line the script moves"). The story's last turn
 * has a room below it: a blank block, rendered last inside the theme's log, that tops what follows the player's line up
 * to a screen. When the player sends, one scroll brings their line near the top of the reading area; the reply then
 * writes downward into the room, which shrinks by what the reply fills, so nothing on screen moves while the narrator
 * writes and the writing line, sticky at the bottom, never rides up with the text. A reply longer than the screen goes
 * on below the fold, and "more below" over the writing line lets the reader follow it in their own time. The send is the
 * only scroll, so a reader who scrolled up to reread is never moved.
 */
const ROOM_TOP = 24; // where the player's line comes to rest, below the top of the reading area

/** The element that scrolls the story: the theme's own panel, or null for the page. */
function storyScroller(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if (o === "auto" || o === "scroll") return p;
  }
  return null;
}

const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** `writer`: a selector for the theme's writing line (the page's bottom bar; ignored when the story has its own panel). */
export function ReadingRoom({ s, writer }: { s: StoryUI; writer: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // "more below": null when hidden, else how far above the bottom of the window it sits (clear of the writing line).
  const [more, setMore] = useState<number | null>(null);
  const live = useRef(false);
  live.current = !!s.live;
  const sent = useRef<string | null>(null);

  const measure = () => {
    const room = ref.current;
    if (!room) return null;
    const box = storyScroller(room);
    const writerH = box ? 0 : document.querySelector<HTMLElement>(writer)?.offsetHeight ?? 0;
    const top = box ? box.getBoundingClientRect().top : 0;
    const bottom = box ? top + box.clientHeight : (window.visualViewport?.height ?? window.innerHeight) - writerH;
    // The latest turn's first line: its data-turn (a theme's turns) or data-pending (the line just sent).
    const all = room.parentElement?.querySelectorAll<HTMLElement>("[data-turn], [data-pending]");
    const anchor = all?.length ? all[all.length - 1]! : null;
    return { room, box, top, bottom, anchor };
  };
  const fit = () => {
    const m = measure();
    if (!m) return;
    const { room, top, bottom, anchor } = m;
    if (!anchor) { room.style.height = "0px"; setMore(null); return; }
    const used = room.getBoundingClientRect().top - anchor.getBoundingClientRect().top;
    room.style.height = `${Math.max(0, Math.floor(bottom - top - ROOM_TOP - used))}px`;
    const end = room.getBoundingClientRect().top; // the room begins where the story ends
    setMore(live.current && end > bottom + 8 ? Math.round(window.innerHeight - bottom) : null);
  };

  // Every render (each line of the reply re-renders the theme), before the browser paints it.
  useLayoutEffect(fit);
  // The send: the one scroll, bringing the player's line near the top of the room. Measured in the next frame, once
  // everything else the send changes on the page has settled.
  useLayoutEffect(() => {
    if (!s.pending) { sent.current = null; return; }
    if (sent.current === s.pending) return;
    sent.current = s.pending;
    fit();
    // Not cancelled when the reply starts (pending clears then, often within the frame): the scroll still happens.
    requestAnimationFrame(() => {
      fit();
      const m = measure();
      if (!m?.anchor) return;
      const lift = m.anchor.getBoundingClientRect().top - (m.top + ROOM_TOP);
      if (Math.abs(lift) > 1) (m.box ?? window).scrollBy({ top: lift, behavior: reducedMotion() ? "auto" : "smooth" });
    });
  }, [s.pending]);
  // What changes size on its own (a plate loading, the writing line growing, a phone's keyboard) and the reader's own
  // scrolling ("more below" goes once the end is in view).
  useEffect(() => {
    const room = ref.current;
    if (!room) return;
    let frame = 0;
    const later = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(later) : null;
    if (room.parentElement) ro?.observe(room.parentElement);
    const bar = document.querySelector<HTMLElement>(writer);
    if (bar) ro?.observe(bar);
    const box = storyScroller(room);
    (box ?? window).addEventListener("scroll", later, { passive: true });
    window.visualViewport?.addEventListener("resize", later);
    window.addEventListener("resize", later);
    return () => {
      cancelAnimationFrame(frame);
      ro?.disconnect();
      (box ?? window).removeEventListener("scroll", later);
      window.visualViewport?.removeEventListener("resize", later);
      window.removeEventListener("resize", later);
    };
  }, [writer]);

  const down = () => {
    const m = measure();
    if (m) (m.box ?? window).scrollBy({ top: Math.max(120, m.bottom - m.top - ROOM_TOP - 48), behavior: reducedMotion() ? "auto" : "smooth" });
  };
  return (
    <>
      <div ref={ref} className="lm-room" aria-hidden="true" />
      {more !== null && (
        <button type="button" className="lm-more-below" style={{ bottom: `${more + 10}px` }} onClick={down}>
          <span aria-hidden="true">↓</span> more below
        </button>
      )}
    </>
  );
}

/** "dream now" for testers on the beta preview. */
export function DreamNow({ s, label = "dream now (beta)" }: { s: StoryUI; label?: string }) {
  if (!s.dreamNow && !s.dreaming) return null;
  return <button type="button" onClick={() => s.dreamNow?.()} disabled={s.dreaming}>{s.dreaming ? "dreaming…" : label}</button>;
}

/** What the story is waiting for: opening, or a look closer on its way (shared by every theme). */
export function StoryStatus({ s }: { s: StoryUI }) {
  if (s.loading) {
    return (
      <div className="lm-opening" role="status" aria-live="polite">
        <span className="lm-opening-weave" aria-hidden="true"><i /><i /><i /></span>
        Opening your story…
      </div>
    );
  }
  if (s.looking) return <div className="lm-looking" role="status" aria-live="polite">Looking closer at {s.looking.thing}…</div>;
  return null;
}

/** Delete a story from the home list: asks in place (no pop-up), then it's gone, with its plates and looks. */
export function DeleteStory({ title, remove, label = "delete" }: { title: string; remove(): Promise<void>; label?: string }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!asking) {
    return <button type="button" className="lm-del" onClick={() => setAsking(true)} aria-label={`Delete “${title}”`}>{label}</button>;
  }
  return (
    <span className="lm-del-ask" role="group" aria-label={`Delete “${title}”?`}>
      <span>{failed ? "Couldn't delete. Try again?" : "Delete for good?"}</span>
      <button type="button" className="lm-del-yes" disabled={busy}
        onClick={() => { setBusy(true); setFailed(false); remove().catch(() => { setFailed(true); setBusy(false); }); }}>{busy ? "deleting…" : "yes, delete"}</button>
      <button type="button" className="lm-del-no" disabled={busy} onClick={() => setAsking(false)}>keep</button>
    </span>
  );
}

/** The rarely used story links, folded away so the footer stays one or two rows (UI review 2). */
export function More({ label = "more…", children }: { label?: string; children: ReactNode }) {
  return (
    <details className="lm-more">
      <summary>{label}</summary>
      <span className="lm-more-items">{children}</span>
    </details>
  );
}

/**
 * Opening truths (co-creation step 3, Ironsworn's Truths): under the first line of a new story, folded away, up to three
 * things that are true here and one thing the player is afraid of. The world keeps the truths from the first turn; the
 * fear shadows the opening and the first chapter's stakes grow from it. All optional; spans, not buttons, in the writer.
 */
function SceneFold({ s }: { s: StoryUI }) {
  const [open, setOpen] = useState(false);
  const [, bump] = useState(0);
  const toggle = () => setOpen((x) => !x);
  const set = (k: number | "fear", v: string) => { if (k === "fear") s.scene.fear = v; else s.scene.truths[k] = v; bump((x) => x + 1); };
  const examples = ["The keeper is my sister", "It has rained here for a year", "No one in this town sleeps"];
  return (
    <div className="lm-scene-fold">
      <span role="button" tabIndex={0} aria-expanded={open} className="lm-scene-toggle" onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}>
        {open ? "▾" : "▸"} Set the scene <span className="lm-scene-opt">(optional)</span>
      </span>
      {open && (
        <div className="lm-scene-fields">
          <p>Up to three things that are true here, and one thing you're afraid of. The world keeps what's true; your fear stays with the storyteller.</p>
          {[0, 1, 2].map((k) => (
            <input key={k} type="text" maxLength={120} value={s.scene.truths[k] ?? ""} onChange={(e) => set(k, e.target.value)}
              placeholder={examples[k]} aria-label={`Something true here (${k + 1} of 3)`} />
          ))}
          <input type="text" maxLength={120} value={s.scene.fear} onChange={(e) => set("fear", e.target.value)}
            placeholder="I'm afraid that she won't know me" aria-label="Something your character is afraid of" />
        </div>
      )}
    </div>
  );
}
